/** Browser notifications + click sound for new mail. */

const SOUND_KEY = "mailstorm.notifySound";
const DESKTOP_KEY = "mailstorm.notifyDesktop";
const ASKED_KEY = "mailstorm.notifyAsked";

export function notifySoundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== "0";
  } catch {
    return true;
  }
}

export function notifyDesktopEnabled(): boolean {
  try {
    return localStorage.getItem(DESKTOP_KEY) !== "0";
  } catch {
    return true;
  }
}

export function setNotifySoundEnabled(on: boolean) {
  localStorage.setItem(SOUND_KEY, on ? "1" : "0");
}

export function setNotifyDesktopEnabled(on: boolean) {
  localStorage.setItem(DESKTOP_KEY, on ? "1" : "0");
}

export function notificationAsked(): boolean {
  try {
    return localStorage.getItem(ASKED_KEY) === "1";
  } catch {
    return false;
  }
}

export function markNotificationAsked() {
  localStorage.setItem(ASKED_KEY, "1");
}

export async function requestNotifyPermission(): Promise<NotificationPermission | "unsupported"> {
  markNotificationAsked();
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/** Short mechanical “clack” via Web Audio (no asset file). */
export function playMailClack() {
  if (!notifySoundEnabled()) return;
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const t0 = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 1800;
    filter.Q.value = 0.8;
    osc.type = "triangle";
    osc.frequency.setValueAtTime(920, t0);
    osc.frequency.exponentialRampToValueAtTime(180, t0 + 0.06);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.1);
    window.setTimeout(() => void ctx.close(), 200);
  } catch {
    /* ignore autoplay blocks */
  }
}

export function showMailNotification(opts: {
  title: string;
  body: string;
  tag?: string;
  onClick?: () => void;
}) {
  if (!notifyDesktopEnabled()) return;
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      tag: opts.tag || "mailstorm-new-mail",
      icon: "/favicon.svg",
      silent: true, // we play our own clack
    });
    n.onclick = () => {
      window.focus();
      opts.onClick?.();
      n.close();
    };
  } catch {
    /* ignore */
  }
}

export function announceNewMail(count: number, accountHint?: string) {
  const body =
    count === 1
      ? `Hai 1 nuova email${accountHint ? ` · ${accountHint}` : ""}`
      : `Hai ${count} nuove email${accountHint ? ` · ${accountHint}` : ""}`;
  playMailClack();
  showMailNotification({
    title: "Mailstorm",
    body,
    tag: `mailstorm-new-${Date.now()}`,
    onClick: () => {
      window.dispatchEvent(new CustomEvent("mailstorm:focus-inbox"));
    },
  });
}
